import type {SharedBulkActionsItemPickerProps} from '@github-ui/item-picker-base/ItemPicker'
import {useCallback} from 'react'
import type {Assignee} from './api/assignee'
import {useUpdateIssueAssigneesBulkMutation} from './mutations/use-update-issue-assignees-bulk-mutation'
import {AssigneeRepositoryPicker, type AssigneePickerProps} from './AssigneePicker'
import type {AssigneePickerBaseAssignee} from '@github-ui/item-picker-assignee'

export type BulkIssueAssigneePickerProps = Omit<AssigneePickerProps, 'id' | 'assignees' | 'onSelectionChange'> & {
  assigneesAppliedToAll: AssigneePickerBaseAssignee[]
  assigneesAppliedToSome: AssigneePickerBaseAssignee[]
  existingIssueAssignees: Map<string, string[]>
  connectionIds: {[key: string]: string[]}
  issueNumbers: number[]
  repository?: {
    databaseId: number | null
    isPrivate: boolean
    isArchived: boolean
  }
  /** Optional externally managed ref to the anchor button for focus return after Copilot modal closes. */
  externalReturnFocusRef?: React.RefObject<HTMLElement>
} & SharedBulkActionsItemPickerProps &
  Parameters<typeof useUpdateIssueAssigneesBulkMutation>[0]

export function BulkIssueAssigneePicker({
  onError,
  onCompleted,
  onMutate,
  issuesToActOn,
  assigneesAppliedToAll,
  assigneesAppliedToSome,
  existingIssueAssignees,
  connectionIds,
  issueNumbers,
  repository,
  ...rest
}: BulkIssueAssigneePickerProps) {
  const {mutate: bulkUpdateMutate} = useUpdateIssueAssigneesBulkMutation({onMutate})

  const onSelectionChange = useCallback(
    (selectedAssignees: Assignee[]) => {
      const assigneesToAdd = selectedAssignees.filter(a => !assigneesAppliedToAll.some(aa => aa.id === a.id))
      const assigneesToRemove = assigneesAppliedToAll.filter(a => !selectedAssignees.some(aa => aa.id === a.id))

      if (!assigneesToAdd.length && !assigneesToRemove.length) {
        return
      }

      const ids = [...issuesToActOn]

      bulkUpdateMutate(
        {
          ids,
          applyAssigneeIds: assigneesToAdd.map(a => a.id),
          removeAssigneeIds: assigneesToRemove.map(a => a.id),
          assignees: selectedAssignees,
        },
        {
          onSuccess: response => onCompleted?.(response.data.updateIssuesBulk.jobId),
          onError,
        },
      )
    },
    [bulkUpdateMutate, issuesToActOn, assigneesAppliedToAll, onCompleted, onError],
  )

  return (
    <AssigneeRepositoryPicker
      {...rest}
      onSelectionChange={onSelectionChange}
      assignees={assigneesAppliedToAll}
      assigneeTokens={[]}
      includeAssignableBots
      includeAuthorableBots={false}
      responsiveOnNarrow
    />
  )
}

try{ BulkIssueAssigneePicker.displayName ||= 'BulkIssueAssigneePicker' } catch {}